import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, RefreshCcw, Filter, TrendingUp, Users, MapPin } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const STATUS_LABELS = {
  new: "New", contacted: "Contacted", meeting_scheduled: "Meeting Sched",
  in_discussion: "In Discussion", pipeline: "In Pipeline",
  won: "Won", loss: "Loss", hold: "Hold",
};

const PERIOD_PRESETS = [
  { key: "weekly",  label: "Weekly (last 7 days)" },
  { key: "monthly", label: "Monthly (last 30 days)" },
  { key: "custom",  label: "Custom Range" },
];

const iso = (d) => d.toISOString().slice(0, 10);
const startOfNDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const today = () => iso(new Date());

export default function SalesReportPage() {
  const [period, setPeriod] = useState("monthly");
  const [fromDate, setFromDate] = useState(startOfNDaysAgo(30));
  const [toDate, setToDate] = useState(today());
  const [executiveId, setExecutiveId] = useState("");
  const [city, setCity] = useState("");
  const [status, setStatus] = useState("all");

  const [executives, setExecutives] = useState([]);
  const [report, setReport] = useState(null);
  const [ledgersReport, setLedgersReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // Adjust from/to when preset changes
  useEffect(() => {
    if (period === "weekly")  { setFromDate(startOfNDaysAgo(7));  setToDate(today()); }
    if (period === "monthly") { setFromDate(startOfNDaysAgo(30)); setToDate(today()); }
  }, [period]);

  useEffect(() => {
    api.get("/sales/executives").then(r => setExecutives(r.data || [])).catch(() => setExecutives([]));
  }, []);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set("from", fromDate);
      if (toDate)   params.set("to", toDate);
      if (executiveId) params.set("executive_id", executiveId);
      if (city.trim()) params.set("city", city.trim());
      if (status && status !== "all") params.set("status", status);
      const { data } = await api.get(`/sales/report?${params.toString()}`);
      setReport(data);
      // Feb 2026 — pull the creator-role separated ledgers so the report can
      // render Sales / Marketing / Combined summary blocks side-by-side.
      try {
        const { data: lr } = await api.get(`/sales/leads-report`);
        setLedgersReport(lr);
      } catch {
        setLedgersReport(null);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Report failed");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, executiveId, city, status]);

  useEffect(() => { run(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const summary = report?.summary || {};
  const execBreakdown = report?.executive_breakdown || [];
  const cityBreakdown = report?.city_breakdown || [];
  const rows = report?.rows || [];

  const download = (kind) => {
    if (!rows.length) { toast.error("No data to export"); return; }
    if (kind === "csv") {
      const headers = ["Client","Contact","Email","Source","Status","Assigned Executive","City","Created","Updated"];
      const lines = [headers.join(",")];
      rows.forEach(r => {
        const cells = [
          r.client_name, r.phone || "", r.email || "", r.source || "",
          STATUS_LABELS[r.status] || r.status, r.assigned_executive_name || "Unassigned",
          r.city || "—", r.created_at ? r.created_at.slice(0, 10) : "",
          r.updated_at ? r.updated_at.slice(0, 10) : "",
        ];
        lines.push(cells.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","));
      });
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `sales_report_${fromDate}_${toDate}.csv`;
      a.click(); URL.revokeObjectURL(url);
      return;
    }
    // pdf
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFont("helvetica", "bold"); doc.setFontSize(16);
    doc.text("Sales Report", 40, 40);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text(`Period: ${fromDate} → ${toDate}`, 40, 58);
    doc.text(`Total: ${summary.total}  ·  Contacted: ${summary.contacted}  ·  In Discussion: ${summary.in_discussion}  ·  In Pipeline: ${summary.pipeline}  ·  Won: ${summary.won}  ·  Loss: ${summary.loss}  ·  Conversion: ${summary.conversion_rate}%`, 40, 74);

    autoTable(doc, {
      startY: 90,
      head: [["Executive", "Total", "Won", "Conv %"]],
      body: execBreakdown.map(r => [r.executive_name, r.total, r.won, `${r.conversion_rate}%`]),
      styles: { fontSize: 8 }, headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] },
    });
    const afterExecY = doc.lastAutoTable.finalY + 15;
    autoTable(doc, {
      startY: afterExecY,
      head: [["City", "Total Leads", "Won"]],
      body: cityBreakdown.map(r => [r.city, r.total, r.won]),
      styles: { fontSize: 8 }, headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] },
    });
    const afterCityY = doc.lastAutoTable.finalY + 15;
    autoTable(doc, {
      startY: afterCityY,
      head: [["Client", "Contact", "Source", "Status", "Assigned", "City", "Created"]],
      body: rows.map(r => [
        r.client_name, r.phone || "—", r.source || "—",
        STATUS_LABELS[r.status] || r.status,
        r.assigned_executive_name || "Unassigned",
        r.city || "—",
        r.created_at ? r.created_at.slice(0, 10) : "",
      ]),
      styles: { fontSize: 7 }, headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] },
    });
    doc.save(`sales_report_${fromDate}_${toDate}.pdf`);
  };

  return (
    <div className="space-y-6" data-testid="sales-report-page">
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Sales · Reports</p>
        <h1 className="font-display text-3xl text-white mt-1">Sales Report</h1>
        <p className="text-xs text-gray-500 mt-1">
          Period-based summary, executive & city breakdowns, and a downloadable lead ledger.
        </p>
      </div>

      {ledgersReport && (
        <LedgerComparisonCard data={ledgersReport} />
      )}

      {/* Filters */}
      <div className="iz-card p-5" data-testid="report-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[#d4af37]"/>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Filters</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Period</p>
            <select className="iz-input w-full" value={period}
                    onChange={(e) => setPeriod(e.target.value)} data-testid="report-period">
              {PERIOD_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">From</p>
            <input type="date" className="iz-input w-full" value={fromDate}
                   onChange={(e) => { setFromDate(e.target.value); setPeriod("custom"); }}
                   data-testid="report-from"/>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">To</p>
            <input type="date" className="iz-input w-full" value={toDate}
                   onChange={(e) => { setToDate(e.target.value); setPeriod("custom"); }}
                   data-testid="report-to"/>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Executive</p>
            <select className="iz-input w-full" value={executiveId}
                    onChange={(e) => setExecutiveId(e.target.value)}
                    data-testid="report-executive">
              <option value="">All executives</option>
              {executives.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">City</p>
            <input className="iz-input w-full" value={city}
                   onChange={(e) => setCity(e.target.value)}
                   placeholder="Bangalore" data-testid="report-city"/>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Status</p>
            <select className="iz-input w-full" value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    data-testid="report-status">
              <option value="all">All statuses</option>
              <option value="contacted">Contacted</option>
              <option value="in_discussion">In Discussion</option>
              <option value="pipeline">In Pipeline</option>
              <option value="won">Won</option>
              <option value="loss">Loss</option>
            </select>
          </div>
          <div className="flex items-end gap-2 lg:col-span-2">
            <button onClick={run} disabled={loading}
                    className="iz-btn-primary text-xs flex items-center gap-2 flex-1"
                    data-testid="report-generate-btn">
              <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/>
              {loading ? "Generating…" : "Generate Report"}
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      {report && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {[
              { label: "Total Leads",   value: summary.total },
              { label: "Contacted",     value: summary.contacted },
              { label: "In Discussion", value: summary.in_discussion },
              { label: "In Pipeline",   value: summary.pipeline },
              { label: "Won",           value: summary.won, gold: true },
              { label: "Loss",          value: summary.loss },
              { label: "Conversion %",  value: `${summary.conversion_rate}%`, gold: true },
            ].map((s) => (
              <div key={s.label} className="iz-card p-4" data-testid={`report-summary-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500">{s.label}</p>
                <p className={`font-display text-2xl mt-2 ${s.gold ? "text-[#d4af37]" : "text-white"}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Executive breakdown */}
          <div className="iz-card p-5" data-testid="report-exec-breakdown">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#d4af37]"/>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Executive Breakdown</p>
            </div>
            {execBreakdown.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No data for this period.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-[10px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#262626]">
                  <tr>
                    <th className="text-left py-2">Executive</th>
                    <th className="text-right py-2">Total</th>
                    <th className="text-right py-2">Won</th>
                    <th className="text-right py-2">Conv %</th>
                  </tr>
                </thead>
                <tbody>
                  {execBreakdown.map(r => (
                    <tr key={r.executive_id} className="border-b border-[#161616]">
                      <td className="py-2 text-gray-200">{r.executive_name}</td>
                      <td className="py-2 text-right text-white">{r.total}</td>
                      <td className="py-2 text-right text-[#d4af37]">{r.won}</td>
                      <td className="py-2 text-right text-gray-300 font-mono">{r.conversion_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* City breakdown */}
          <div className="iz-card p-5" data-testid="report-city-breakdown">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-[#d4af37]"/>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">City Breakdown</p>
            </div>
            {cityBreakdown.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No data for this period.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-[10px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#262626]">
                  <tr>
                    <th className="text-left py-2">City</th>
                    <th className="text-right py-2">Total Leads</th>
                    <th className="text-right py-2">Won</th>
                  </tr>
                </thead>
                <tbody>
                  {cityBreakdown.map((r, i) => (
                    <tr key={i} className="border-b border-[#161616]">
                      <td className="py-2 text-gray-200">{r.city}</td>
                      <td className="py-2 text-right text-white">{r.total}</td>
                      <td className="py-2 text-right text-[#d4af37]">{r.won}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Lead table */}
          <div className="iz-card p-5" data-testid="report-lead-table">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#d4af37]"/>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Lead Details ({rows.length})</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => download("csv")}
                        className="iz-btn-secondary text-xs flex items-center gap-1.5"
                        data-testid="report-download-csv">
                  <FileSpreadsheet className="w-3.5 h-3.5"/> CSV
                </button>
                <button onClick={() => download("pdf")}
                        className="iz-btn-primary text-xs flex items-center gap-1.5"
                        data-testid="report-download-pdf">
                  <FileText className="w-3.5 h-3.5"/> PDF
                </button>
              </div>
            </div>
            {rows.length === 0 ? (
              <p className="text-xs text-gray-500 italic py-6 text-center">No leads match your filters.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#262626]">
                    <tr>
                      <th className="text-left py-2 px-2">Client</th>
                      <th className="text-left py-2 px-2">Contact</th>
                      <th className="text-left py-2 px-2">Source</th>
                      <th className="text-left py-2 px-2">Status</th>
                      <th className="text-left py-2 px-2">Assigned</th>
                      <th className="text-left py-2 px-2">City</th>
                      <th className="text-left py-2 px-2">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.id} className="border-b border-[#161616] hover:bg-[#0f0f0f]">
                        <td className="py-2 px-2 text-white">{r.client_name}</td>
                        <td className="py-2 px-2 text-gray-400 font-mono">{r.phone || "—"}</td>
                        <td className="py-2 px-2 text-gray-300">{r.source || "—"}</td>
                        <td className="py-2 px-2 text-gray-300">{STATUS_LABELS[r.status] || r.status}</td>
                        <td className="py-2 px-2 text-gray-300">{r.assigned_executive_name || <span className="text-[#d4af37]">Unassigned</span>}</td>
                        <td className="py-2 px-2 text-gray-300">{r.city || "—"}</td>
                        <td className="py-2 px-2 text-gray-500 font-mono">{r.created_at ? r.created_at.slice(0, 10) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Feb 2026 · Side-by-side Sales vs Marketing vs Combined summary powered by
 * `/api/sales/leads-report`. Renders three columns of the same 7 status
 * counters so the operator can compare the two ledgers at a glance. */
function LedgerComparisonCard({ data }) {
  const cols = [
    { key: "sales",     label: "Sales Ledger",       totals: data.sales?.totals || {} },
    { key: "marketing", label: "Marketing Ledger",   totals: data.marketing?.totals || {} },
    { key: "combined",  label: "Combined Total",     totals: data.combined_totals || {}, highlight: true },
  ];
  const metrics = [
    ["total",         "Total Leads"],
    ["unassigned",    "Unassigned"],
    ["contacted",     "Contacted"],
    ["in_discussion", "In Discussion"],
    ["pipeline",      "In Pipeline"],
    ["won",           "Won"],
    ["loss",          "Loss"],
    ["pending_claim", "Pending Claim"],
  ];
  return (
    <div className="iz-card p-5" data-testid="ledger-comparison-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
            Ledger Comparison · Sales vs Marketing
          </p>
          <p className="text-xs text-gray-500 mt-0.5 italic">
            Separated by creator role at time of lead creation. Combined column adds both ledgers.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cols.map((c) => (
          <div key={c.key}
               className={`border rounded p-4 ${c.highlight ? "border-[#d4af37]/60 bg-[#d4af37]/5" : "border-[#1a1a1a] bg-[#0a0a0a]"}`}
               data-testid={`ledger-col-${c.key}`}>
            <p className={`text-[10px] font-mono uppercase tracking-widest mb-3 ${c.highlight ? "text-[#d4af37]" : "text-gray-400"}`}>
              {c.label}
            </p>
            <ul className="space-y-1.5">
              {metrics.map(([k, label]) => (
                <li key={k} className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">{label}</span>
                  <span className={`font-mono font-medium ${c.highlight ? "text-[#d4af37]" : "text-white"}`}>
                    {c.totals[k] ?? 0}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

